import {
  HHCard,
  HHCardContent,
  HHCardHeader,
  HHStack,
  HHTextField,
} from '@hinge-health/react-component-library';
import AssessmentOutlined from '@mui/icons-material/AssessmentOutlined';
import BlurLinear from '@mui/icons-material/BlurLinear';
import BuildCircleOutlined from '@mui/icons-material/BuildCircleOutlined';
import CalculateOutlined from '@mui/icons-material/CalculateOutlined';
import FileUploadOutlined from '@mui/icons-material/FileUploadOutlined';
import FormatListBulletedOutlined from '@mui/icons-material/FormatListBulletedOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import LanguageOutlined from '@mui/icons-material/LanguageOutlined';
import LocalOfferOutlined from '@mui/icons-material/LocalOfferOutlined';
import MedicalInformationOutlined from '@mui/icons-material/MedicalInformationOutlined';
import PauseCircleOutlineOutlined from '@mui/icons-material/PauseCircleOutlineOutlined';
import PendingActionsOutlined from '@mui/icons-material/PendingActionsOutlined';
import PercentOutlined from '@mui/icons-material/PercentOutlined';
import PinOutlined from '@mui/icons-material/PinOutlined';
import PriceCheckOutlined from '@mui/icons-material/PriceCheckOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SendOutlined from '@mui/icons-material/SendOutlined';
import SummarizeOutlined from '@mui/icons-material/SummarizeOutlined';
import { InputAdornment } from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LocalStorage } from '../../../utils/local-storage';
import { routes } from '../constants/strings/routes';
import { useBillingWriteAccess } from '../hooks/use-billing-write-access';
import { BILL_DATE_FORMAT } from './bills/bill-grid-components/bill-management-types';
import { BillManagementNavigator } from './bills/bill-management/bill-management-navigator';
import { GridLevelName } from './grid-components/grid-types';
import { QuickActionButton } from './quick-action-button';

const PINNED_STORAGE_KEY = 'quickActionPinnedItems';

interface QuickAction {
  label: string;
  ariaLabel: string;
  Icon: React.ElementType;
  onClick: () => void;
  visible?: boolean;
}

interface QuickActionSection {
  title: string;
  actions: QuickAction[];
}

const default90DayLookbackStart = dayjs().subtract(90, 'days').toDate();

export const QuickActionsGrid = (): JSX.Element => {
  const navigate = useNavigate();
  const canWrite = useBillingWriteAccess();

  const [searchQuery, setSearchQuery] = useState('');
  const [pinnedItems, setPinnedItems] = useState<string[]>(() => {
    const stored = LocalStorage.get(PINNED_STORAGE_KEY);

    return Array.isArray(stored) ? stored : [];
  });

  const togglePin = (ariaLabel: string): void => {
    setPinnedItems(prev => {
      const next = prev.includes(ariaLabel)
        ? prev.filter(item => item !== ariaLabel)
        : [...prev, ariaLabel];
      LocalStorage.set(PINNED_STORAGE_KEY, next);
      return next;
    });
  };

  const sections: QuickActionSection[] = [
    {
      title: 'Bills',
      actions: [
        {
          label: 'Pending Bills',
          ariaLabel: 'go to pending bills',
          Icon: PendingActionsOutlined,
          onClick: (): void => {
            BillManagementNavigator(
              {
                held: false,
                submitted: false,
                startDate: dayjs(default90DayLookbackStart).format(
                  BILL_DATE_FORMAT,
                ),
              },
              navigate,
            );
          },
        },
        {
          label: 'Submitted Bills',
          ariaLabel: 'go to submitted bills',
          Icon: SendOutlined,
          onClick: (): void => {
            BillManagementNavigator(
              {
                held: false,
                submitted: true,
                startDate: dayjs(default90DayLookbackStart).format(
                  BILL_DATE_FORMAT,
                ),
              },
              navigate,
            );
          },
        },
        {
          label: 'Held Bills',
          ariaLabel: 'go to held bills',
          Icon: PauseCircleOutlineOutlined,
          onClick: (): void => {
            BillManagementNavigator(
              {
                held: true,
                submitted: false,
                startDate: dayjs(default90DayLookbackStart).format(
                  BILL_DATE_FORMAT,
                ),
              },
              navigate,
            );
          },
        },
        {
          label: 'All Submissions',
          ariaLabel: 'go to all submissions',
          Icon: FormatListBulletedOutlined,
          onClick: (): void => {
            BillManagementNavigator(
              {
                submissionStartDate: dayjs(default90DayLookbackStart).format(
                  BILL_DATE_FORMAT,
                ),
                submissionEndDate: dayjs().format(BILL_DATE_FORMAT),
              },
              navigate,
              GridLevelName.SUBMISSION,
            );
          },
        },
      ],
    },
    {
      title: 'Reports & Tools',
      actions: [
        {
          label: 'Unbilled Report',
          ariaLabel: 'go to unbilled report',
          Icon: AssessmentOutlined,
          onClick: (): void => {
            navigate(`${routes.billing.home}${routes.billing.unbilledReport}`);
          },
        },
        {
          label: 'Aggregate Holds',
          ariaLabel: 'go to aggregate holds',
          Icon: SummarizeOutlined,
          onClick: (): void => {
            navigate(`${routes.billing.home}${routes.billing.holdsAggregate}`);
          },
        },
        {
          label: 'Billing Tool',
          ariaLabel: 'go to billing tool',
          Icon: BuildCircleOutlined,
          onClick: (): void => {
            navigate(`${routes.billing.home}/tool`);
          },
        },
        {
          label: 'Discount Calculator',
          ariaLabel: 'go to discount calculator',
          Icon: CalculateOutlined,
          onClick: (): void => {
            navigate(
              `${routes.billing.home}/${routes.billing.discountCalculator}`,
            );
          },
        },
        {
          label: 'CSV Upload',
          ariaLabel: 'go to csv upload',
          Icon: FileUploadOutlined,
          onClick: (): void => {
            navigate(`${routes.billing.home}${routes.billing.csvUpload}`);
          },
          visible: canWrite,
        },
      ],
    },
    {
      title: 'Configuration',
      actions: [
        {
          label: 'Client Insurers',
          ariaLabel: 'go to client insurers',
          Icon: LanguageOutlined,
          onClick: (): void => {
            navigate(`${routes.billing.main}`);
          },
        },
        {
          label: 'Program Caps',
          ariaLabel: 'go to program caps',
          Icon: LocalOfferOutlined,
          onClick: (): void => {
            navigate(`${routes.billing.home}${routes.billing.programCaps}`);
          },
        },
        {
          label: 'Fee Schedules',
          ariaLabel: 'go to fee schedules',
          Icon: PriceCheckOutlined,
          onClick: (): void => {
            navigate(
              `${routes.billing.home}${routes.billing.clientFeeSchedules}`,
            );
          },
        },
        {
          label: 'CPT Codes',
          ariaLabel: 'go to cpt codes',
          Icon: PinOutlined,
          onClick: (): void => {
            navigate(`${routes.billing.home}${routes.billing.procedureCodes}`);
          },
        },
        {
          label: 'ICD Codes',
          ariaLabel: 'go to icd codes',
          Icon: MedicalInformationOutlined,
          onClick: (): void => {
            navigate(`${routes.billing.home}${routes.billing.icdCodes}`);
          },
        },
        {
          label: 'Revenue Shares',
          ariaLabel: 'go to revenue shares',
          Icon: PercentOutlined,
          onClick: (): void => {
            navigate(`${routes.billing.home}${routes.billing.revenueShares}`);
          },
        },
        {
          label: 'Packages',
          ariaLabel: 'go to packages',
          Icon: Inventory2Outlined,
          onClick: (): void => {
            navigate(`${routes.billing.home}${routes.billing.packages}`);
          },
        },
        {
          label: 'Volume Based Discounts',
          ariaLabel: 'go to volume based discounts',
          Icon: BlurLinear,
          onClick: (): void => {
            navigate(
              `${routes.billing.home}${routes.billing.volumeBasedDiscounts}`,
            );
          },
        },
      ],
    },
  ];

  const allActions = sections
    .flatMap(s => s.actions)
    .filter(a => a.visible !== false);

  const pinnedActions = allActions.filter(a =>
    pinnedItems.includes(a.ariaLabel),
  );

  const matchesSearch = (action: QuickAction): boolean =>
    !searchQuery ||
    action.label.toLowerCase().includes(searchQuery.toLowerCase());

  const filteredPinnedActions = pinnedActions.filter(matchesSearch);

  const unpinnedActions = allActions
    .filter(a => !pinnedItems.includes(a.ariaLabel))
    .filter(matchesSearch)
    .sort((a, b) => a.label.localeCompare(b.label));

  const renderActionGrid = (actions: QuickAction[]): JSX.Element => (
    <HHStack
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(3, 1fr)',
          md: 'repeat(5, 1fr)',
        },
        gap: 2,
      }}
    >
      {actions.map(action => (
        <QuickActionButton
          key={action.ariaLabel}
          label={action.label}
          ariaLabel={action.ariaLabel}
          Icon={action.Icon}
          onClick={action.onClick}
          pinned={pinnedItems.includes(action.ariaLabel)}
          onTogglePin={(): void => togglePin(action.ariaLabel)}
        />
      ))}
    </HHStack>
  );

  return (
    <HHStack spacing={3}>
      <HHTextField
        data-testid="quick-actions-search-input"
        hhVariant="variant-bypass"
        label=""
        placeholder="Search actions..."
        value={searchQuery}
        onChange={(e): void => setSearchQuery(e.target.value)}
        size="small"
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlinedIcon sx={{ color: 'action.active' }} />
            </InputAdornment>
          ),
        }}
      />

      {filteredPinnedActions.length > 0 && (
        <HHCard sx={{ border: '1px solid lightgray', boxShadow: 'none' }}>
          <HHCardHeader
            title="Pinned"
            titleTypographyProps={{ variant: 'body1', paddingRight: 8 }}
            sx={{ paddingBottom: 0 }}
          />
          <HHCardContent>
            {renderActionGrid(filteredPinnedActions)}
          </HHCardContent>
        </HHCard>
      )}

      {unpinnedActions.length > 0 && renderActionGrid(unpinnedActions)}
    </HHStack>
  );
};
